import React, { useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import { api, API_URL } from "../lib/api";
import { toast } from "sonner";
import { Camera, X, Plus, Lock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { cloudyThumb, cloudyLogo } from "../lib/cloudy";

// Allowed mime types for the native <input type="file"> accept attribute.
// Kept broad so iOS shows HEIC/HEIF photos and AVIF/WebP from other sources.
const ACCEPT_MIME = "image/*,image/heic,image/heif,image/avif,.heic,.heif,.avif,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tiff";
const MAX_BYTES = 15 * 1024 * 1024; // 15 MB avant compression

/**
 * Normalise un fichier image avant upload : limite 15 MB, compression
 * canvas vers JPEG ≤ 1.5 MB / 1600px. Si la compression échoue (navigateur
 * trop ancien ou HEIC non supporté), on upload le fichier brut — Cloudinary
 * gère HEIC côté serveur.
 */
async function prepareFile(file) {
  if (!file) throw new Error("Aucun fichier");
  if (file.size > MAX_BYTES) {
    throw new Error("Fichier trop lourd (max 15 Mo). Essayez une version plus petite.");
  }
  try {
    const compressed = await imageCompression(file, {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 1600,
      useWebWorker: true,
      fileType: "image/jpeg",
      initialQuality: 0.85,
    });
    return compressed;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Image compression failed, uploading raw:", e);
    return file;
  }
}

/** Signed direct upload from browser to Cloudinary. */
async function uploadFile(rawFile, slug, kind) {
  const file = await prepareFile(rawFile);
  let sig;
  try {
    sig = await api.get("/dashboard/upload/signature", { params: { slug, kind } }).then((r) => r.data);
  } catch (e) {
    const detail = e?.response?.data?.detail;
    if (detail) throw new Error(detail);
    throw new Error("Impossible d'obtenir l'autorisation d'upload — déconnectez-vous puis reconnectez-vous.");
  }
  const form = new FormData();
  form.append("file", file);
  form.append("api_key", sig.api_key);
  form.append("timestamp", sig.timestamp);
  form.append("signature", sig.signature);
  form.append("folder", sig.folder);
  let r;
  try {
    r = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: "POST", body: form });
  } catch (netErr) {
    throw new Error("Réseau indisponible — vérifiez votre connexion");
  }
  if (!r.ok) {
    let cldMsg = "";
    try {
      const payload = await r.json();
      cldMsg = payload?.error?.message || "";
    } catch (err) {
      console.debug("[ImageUpload] could not parse Cloudinary error response", err);
    }
    throw new Error(cldMsg ? `Cloudinary : ${cldMsg}` : `Cloudinary a refusé l'upload (${r.status})`);
  }
  const data = await r.json();
  return data.secure_url;
}

export function PhotoGallery({ slug, photos, onChange, tier = "free" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const locked = tier === "free";

  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadFile(file, slug, "photos");
      const res = await api.post(`/dashboard/businesses/${slug}/photos`, { url });
      onChange(res.data.photos);
      toast.success("Photo ajoutée");
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.detail || "Upload échoué");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removePhoto = async (url) => {
    if (!window.confirm("Supprimer cette photo ? Cette action est définitive.")) return;
    try {
      const res = await api.delete(`/dashboard/businesses/${slug}/photos`, { params: { url } });
      onChange(res.data.photos);
      toast.success("Photo supprimée");
    } catch (err) {
      console.debug("[PhotoGallery] delete failed", err);
      toast.error("Suppression échouée");
    }
  };

  if (locked) {
    return (
      <div data-testid="photo-gallery-locked" className="rounded-sm border border-dashed border-border bg-accent/20 p-6 text-center">
        <Lock size={28} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
        <div className="label-uppercase mb-1">Galerie photos — Premium</div>
        <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
          Les photos de votre commerce sont réservées aux partenaires Premium. Votre logo reste modifiable en version gratuite.
        </p>
        <Link
          to="/passez-partenaire"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-[#0f3d29] transition-colors"
          data-testid="photo-gallery-upgrade-cta"
        >
          Activer Premium
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="photo-gallery">
      <div className="label-uppercase mb-3">Galerie photos ({photos.length}/8)</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {photos.map((p) => (
          <div key={p} className="relative group aspect-square bg-muted border border-border rounded-sm overflow-hidden" data-testid={`photo-${p}`}>
            <img src={cloudyThumb(p)} alt="" className="w-full h-full object-cover" loading="lazy" />
            <button
              onClick={() => removePhoto(p)}
              className="absolute top-1 right-1 bg-destructive text-destructive-foreground p-1.5 rounded-sm shadow-md md:opacity-70 md:group-hover:opacity-100 transition-opacity"
              title="Supprimer cette photo"
              aria-label="Supprimer cette photo"
              data-testid="remove-photo"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        ))}
        {photos.length < 8 && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="aspect-square border border-dashed border-border rounded-sm grid place-items-center text-muted-foreground hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
            data-testid="upload-photo-btn"
          >
            {busy ? "…" : <Plus size={24} />}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept={ACCEPT_MIME} onChange={addPhoto} className="hidden" data-testid="photo-input"/>
    </div>
  );
}

export function LogoUploader({ slug, logo, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadFile(file, slug, "logos");
      await api.put(`/dashboard/businesses/${slug}/logo`, { url });
      onChange(url);
      toast.success("Logo mis à jour");
    } catch (err) {
      toast.error(err?.message || "Upload échoué");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer votre logo ? Votre fiche affichera une icône générique à la place.")) return;
    setBusy(true);
    try {
      await api.put(`/dashboard/businesses/${slug}/logo`, { url: null });
      onChange(null);
      toast.success("Logo supprimé");
    } catch (err) {
      console.debug("[LogoUploader] delete failed", err);
      toast.error(err?.response?.data?.detail || "Suppression échouée");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="logo-uploader">
      <div className="label-uppercase mb-3">Logo</div>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-20 h-20 bg-muted border border-border rounded-sm overflow-hidden grid place-items-center text-muted-foreground shrink-0">
          {logo ? <img src={cloudyLogo(logo)} alt="logo" className="w-full h-full object-cover" loading="lazy"/> : <Camera size={24} />}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="border border-border hover:border-foreground px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-50"
            data-testid="upload-logo-btn"
          >
            {busy ? "Upload…" : (logo ? "Changer le logo" : "Ajouter un logo")}
          </button>
          {logo && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="text-sm font-semibold text-destructive hover:underline px-2 py-2 disabled:opacity-50"
              data-testid="remove-logo-btn"
            >
              Supprimer
            </button>
          )}
        </div>
      </div>
      <input ref={inputRef} type="file" accept={ACCEPT_MIME} onChange={upload} className="hidden" data-testid="logo-input"/>
    </div>
  );
}
